// @flow

import { ReactComponent as ArrowDown } from 'assets/icons/svg/icon_arrow_down.svg';
import {
  RoundFlagBR,
  RoundFlagDE,
  RoundFlagES,
  RoundFlagEN,
  RoundFlagFR,
  RoundFlagIT,
  RoundFlagPT,
} from 'assets/images/flag';
import config from 'config/app.config';
import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import styled, { css } from 'styled-components';
import { Locales } from 'types/common';
import { Licenses } from 'types/common';

import { getLang } from '../../../i18n/l10n';
import { SET_LANGUAGE } from '../../../store/actions';
import { media } from '../../../styles/GlobalStyle';

const FLAG_SIZE_MOBILE = 22;
const FLAG_SIZE_DESKTOP = 28;

const FLAG_SPACE_MOBILE = 10;
const FLAG_SPACE_DESKTOP = 15;

const FlagByLocales = {
  [Locales.BR]: RoundFlagBR,
  [Locales.DE]: RoundFlagDE,
  [Locales.ES]: RoundFlagES,
  [Locales.EN]: RoundFlagEN,
  [Locales.FR]: RoundFlagFR,
  [Locales.IT]: RoundFlagIT,
  [Locales.PT]: RoundFlagPT,
};

const availableLocalesByLicences = {
  [Licenses.ES]: [Locales.EN, Locales.ES],
};

const Wrapper = styled.div`
  display: flex;
`;

const StyledArrow = styled(ArrowDown)`
  width: 12px;
  margin-left: 5px;
  margin-top: 2.5px;

  ${media.greaterThan('desktop')`
    width: 25px;
    margin-top: 4px;
  `}

  cursor: pointer;

  transition: transform 150ms ease-in-out;

  ${({ $isOpen }) =>
    $isOpen
    && css`
      transform: rotate(180deg);
    `}
`;

const FlagsWrapper = styled.div`
  width: ${FLAG_SIZE_MOBILE}px;
  height: ${FLAG_SIZE_MOBILE}px;
  border-radius: ${FLAG_SIZE_MOBILE}px;
  padding: 2px;

  border: 1px solid #292e3d;
  background: #0a0b0f;

  overflow: hidden;
  box-sizing: content-box;

  ${media.greaterThan('desktop')`
    width: ${FLAG_SIZE_DESKTOP}px;
    height: ${FLAG_SIZE_DESKTOP}px;
  `}

  transition: height 150ms ease-in-out;

  ${({ $size, $isOpen }) =>
    $isOpen
    && css`
      height: calc(
        ${$size} * (${FLAG_SIZE_MOBILE}px + ${FLAG_SPACE_MOBILE}px) - ${FLAG_SPACE_MOBILE}px
      );

      ${media.greaterThan('desktop')`
        height: calc(${$size} * (${FLAG_SIZE_DESKTOP}px + ${FLAG_SPACE_DESKTOP}px) - ${FLAG_SPACE_DESKTOP}px);
      `}
    `}
`;

const Flag = styled.div`
  display: block;

  width: ${FLAG_SIZE_MOBILE}px;
  height: ${FLAG_SIZE_MOBILE}px;

  margin-bottom: ${FLAG_SPACE_MOBILE}px;
  border-radius: 100%;
  cursor: pointer;

  ${media.greaterThan('desktop')`
    width: ${FLAG_SIZE_DESKTOP}px;
    height: ${FLAG_SIZE_DESKTOP}px;
    margin-bottom: ${FLAG_SPACE_DESKTOP}px;
  `}
`;

type LangSwitcherProps = {
  className?: string,
};

const LangSwitcher = ({ className }: LangSwitcherProps) => {
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);

  const locales = useMemo(() => {
    const lang = getLang();
    const locales = availableLocalesByLicences[config.licence]?.filter(l => l !== lang);
    if (locales) {
      locales.unshift(lang);
    }

    return locales;
  }, []);

  const onClickArrow = useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen]);

  const onClickFlag = useCallback(
    (locale) => {
      if (isOpen) {
        dispatch({
          type: SET_LANGUAGE,
          payload: locale,
        });
      }
    },
    [isOpen],
  );

  if (Object.keys(availableLocalesByLicences).indexOf(config.licence) === -1) {
    return null;
  }

  return (
    <>
      <Wrapper className={className}>
        <FlagsWrapper
          $size={locales.length}
          $isOpen={isOpen}
        >
          {locales.map(locale => (
            <Flag
              key={`lang-switcher-flag-${locale}`}
              as={FlagByLocales[locale]}
              onClick={() => onClickFlag(locale)}
            />
          ))}
        </FlagsWrapper>
        <StyledArrow
          onClick={onClickArrow}
          $isOpen={isOpen}
        />
      </Wrapper>
    </>
  );
};

export default LangSwitcher;
